import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { TripEntity } from './trip.entity';

/**
 * Audit row for a stop the Dispatch wizard tried to commit onto
 * a trip but had to skip — see Phase D / D5.
 *
 * Sources:
 *  - **CC auth failures** (Phase C Rule 2): the gateway refused
 *    the pre-auth on the client's default credit card.
 *  - **Rule 1 defense-in-depth**: client failed
 *    auto-gen-eligibility at commit time (not_auto_gen,
 *    no_default_payment_method, full_pallet_only).
 *  - **Rule 3 defense-in-depth**: client `nightTimeEligibility`
 *    incompatible with route `timeClassification`.
 *  - **Validation errors**: invalid input shape, missing client,
 *    invalid line quantity, etc.
 *  - **Orphan attachment errors**: existing order not found, or
 *    already attached to another trip stop.
 *
 * The wizard's commit-result page surfaces these in real time;
 * this entity persists them so a manager (or salesperson) viewing
 * the trip days later can still see "this trip had 3 stops, plus
 * 2 attempted stops that were skipped because of X" without
 * digging through wizard logs.
 *
 * Reason values mirror `DispatchCommitSkippedStopDto.reason` from
 * `libs/shared/src/models/dto/dispatch.dto.ts`. Stored as a
 * varchar (not a TypeORM enum) so adding new reasons in the future
 * doesn't require a schema migration — the column accepts any
 * string and the DTO union enforces shape at the boundary.
 */
@Entity('trip_commit_skip')
@Index('idx_trip_commit_skip_trip', ['tripId'])
export class TripCommitSkipEntity extends BaseEntity {
	@Column({ name: 'fk_trip__id', type: 'int', unsigned: true })
	public tripId!: number;

	@Column({
		name: 'fk_client__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public clientId!: number | null;

	@Column({ name: 'clientName', type: 'varchar', length: 255, nullable: true })
	public clientName!: string | null;

	@Column({ name: 'reason', type: 'varchar', length: 64 })
	public reason!: string;

	@Column({ name: 'detail', type: 'text', nullable: true })
	public detail!: string | null;

	@ManyToOne(() => TripEntity, { onDelete: 'CASCADE' })
	@JoinColumn({ name: 'fk_trip__id' })
	public fkTrip?: TripEntity;

	@ManyToOne(() => ClientEntity, { nullable: true, onDelete: 'SET NULL' })
	@JoinColumn({ name: 'fk_client__id' })
	public fkClient?: ClientEntity;
}
